import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '综艺', to: '/category/variety' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const kw = keyword.trim();
    setMenuOpen(false);
    setMobileSearchOpen(false);
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-100/95 backdrop-blur border-b border-background-200/80'
          : 'bg-gradient-to-b from-background-50/95 to-background-50/40 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[68px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-950 shadow-sm transition-transform group-hover:scale-105">
              <i className="ri-heart-3-fill text-lg" />
            </span>
            <span className="font-heading text-lg md:text-xl font-extrabold tracking-tight text-foreground-950">
              红桃<span className="text-primary-500">视频</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-primary-400 bg-primary-500/10'
                      : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex items-center relative w-64 lg:w-72">
            <span className="absolute left-3 w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、综艺"
              className="w-full h-10 pl-9 pr-3 rounded-md bg-background-200/70 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 transition"
            />
          </form>

          <div className="flex md:hidden items-center gap-1">
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              aria-label="搜索"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-200/70 cursor-pointer"
            >
              <i className="ri-search-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="菜单"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-200/70 cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/80 bg-background-100/98 px-4 py-3">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium ${
                    isActive ? 'text-primary-400 bg-primary-500/10' : 'text-foreground-800'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      {mobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-background-50/98 backdrop-blur px-4 pt-6">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入关键词搜索"
                className="w-full h-12 pl-11 pr-3 rounded-md bg-background-200/80 border border-background-300/60 text-base text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="button"
              onClick={() => setMobileSearchOpen(false)}
              className="px-3 py-3 text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
            >
              取消
            </button>
          </form>
          <button
            type="submit"
            onClick={submitSearch}
            className="mt-4 w-full h-11 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </div>
      )}
    </header>
  );
}